import * as THREE from "three";

/**
 * Local-only ambient VFX (never networked): fireflies, petals, butterflies, pollen, mist,
 * plus the occasional sweeping leaf gust across the park.
 * Sparse cloud around the local pup; each mote scales + fades in/out over its life.
 */
export class AmbientVfx {
  readonly root = new THREE.Group();
  private readonly layers: Layer[] = [];
  private readonly mapW: number;
  private readonly mapH: number;
  private seeded = false;
  private tickParity = 0;
  private lowQuality = false;
  private lastPx = 0;
  private lastPz = 0;
  private moveX = 0;
  private moveZ = 0;
  private gust: LeafGust | null = null;
  /** Seconds until the next park-wide leaf sweep. */
  private gustIn = 8 + Math.random() * 10;
  /** Always-on autumn leaves tumbling around the pup at ground level. */
  private readonly driftField: LeafDriftField;

  constructor(mapW: number, mapH: number) {
    this.mapW = mapW;
    this.mapH = mapH;

    // Near glow + a deep / tall far field so motes read through the upper frame
    // (canopy / horizon), not only around the pup's feet.
    this.addLayer(42, 0xfff3b0, 6.8, true, "firefly", 0.7, 5.4);
    this.addLayer(22, 0xffd080, 5.2, true, "firefly", 1.0, 4.6);
    this.addLayer(40, 0xffe9a0, 5.0, true, "fireflyFar", 2.2, 9.5);
    this.addLayer(28, 0xffc878, 4.2, true, "fireflyFar", 3.0, 8.2);
    this.addLayer(16, 0xffb8c8, 6.5, false, "petal", 1.2, 6.5);
    this.addLayer(12, 0xf0d4ff, 6, true, "butterfly", 1.4, 6.0);
    this.addLayer(22, 0xfff0c8, 4.1, true, "pollen", 0.5, 4.2);
    this.addLayer(9, 0xfff0e0, 9.5, true, "mist", 0.06, 0.55);
    this.addLayer(7, 0xfff8e8, 11, true, "mist", 2.5, 7.5);
    this.driftField = createLeafDriftField(44);
    this.root.add(this.driftField.points);
  }

  setLowQuality(on: boolean): void {
    this.lowQuality = on;
    this.driftField.lowQuality = on;
    const driftMat = this.driftField.points.material as THREE.ShaderMaterial;
    driftMat.uniforms.uSize!.value = on ? 10 : 13;
  }

  /**
   * Kick petals + fireflies when a pup trots through a flower bed.
   * `intensity` ~0.35 walk / ~1.0 Zoomies.
   */
  stirFlowerBed(x: number, z: number, intensity: number): void {
    if (this.lowQuality && intensity < 0.7) {
      return;
    }
    const i = Math.max(0.2, Math.min(1.4, intensity));
    const petalN = Math.round(3 + i * 9);
    const flyN = Math.round(2 + i * 11);
    for (const layer of this.layers) {
      if (layer.kind === "petal") {
        burstMotes(layer, x, z, petalN, i, "petal");
      } else if (layer.kind === "firefly") {
        burstMotes(layer, x, z, flyN, i, "firefly");
      } else if (layer.kind === "pollen" && i > 0.6) {
        burstMotes(layer, x, z, Math.round(2 + i * 4), i * 0.7, "pollen");
      }
    }
  }

  /** `playerX` / `playerZ` are world coords of the local pup (or camera focus as fallback).
   * Optional `camX` / `camZ` bias far motes toward the upper frame (away from the lens). */
  update(dtSec: number, playerX: number, playerZ: number, camX?: number, camZ?: number): void {
    if (!this.seeded) {
      this.lastPx = playerX;
      this.lastPz = playerZ;
      this.seedNearPlayer(playerX, playerZ, camX, camZ);
      this.seeded = true;
    }

    const dx = playerX - this.lastPx;
    const dz = playerZ - this.lastPz;
    this.moveX = this.moveX * 0.85 + dx * 0.15;
    this.moveZ = this.moveZ * 0.85 + dz * 0.15;
    this.lastPx = playerX;
    this.lastPz = playerZ;

    this.tickParity ^= 1;
    if (this.lowQuality && this.tickParity === 0) {
      this.updateGust(dtSec * 2, playerX, playerZ);
      return;
    }

    const dt = Math.min(0.05, Math.max(0.001, dtSec));
    const heading =
      this.moveX * this.moveX + this.moveZ * this.moveZ > 1e-6
        ? Math.atan2(this.moveZ, this.moveX)
        : null;
    const awayFromCam =
      camX !== undefined && camZ !== undefined
        ? Math.atan2(playerZ - camZ, playerX - camX)
        : null;
    for (const layer of this.layers) {
      stepLayer(layer, dt, playerX, playerZ, this.mapW, this.mapH, heading, awayFromCam);
    }
    stepLeafDriftField(this.driftField, dt, playerX, playerZ, this.mapW, this.mapH);
    this.updateGust(dt, playerX, playerZ);
  }

  destroy(): void {
    this.clearGust();
    this.driftField.points.removeFromParent();
    this.driftField.points.geometry.dispose();
    const driftMat = this.driftField.points.material;
    if (driftMat instanceof THREE.Material) {
      driftMat.dispose();
    }
    for (const layer of this.layers) {
      layer.points.geometry.dispose();
      const mat = layer.points.material;
      if (mat instanceof THREE.Material) {
        mat.dispose();
      }
    }
    this.layers.length = 0;
    this.root.removeFromParent();
  }

  private updateGust(dt: number, playerX: number, playerZ: number): void {
    if (this.gust) {
      stepLeafGust(this.gust, dt);
      if (this.gust.age >= this.gust.duration) {
        this.clearGust();
        this.gustIn = 12 + Math.random() * 16;
      }
      return;
    }
    this.gustIn -= dt;
    if (this.gustIn > 0) {
      return;
    }
    this.gust = makeLeafGust(this.mapW, this.mapH, playerX, playerZ, this.lowQuality);
    this.root.add(this.gust.points);
  }

  private clearGust(): void {
    if (!this.gust) {
      return;
    }
    this.gust.points.removeFromParent();
    this.gust.points.geometry.dispose();
    const mat = this.gust.points.material;
    if (mat instanceof THREE.Material) {
      mat.dispose();
    }
    this.gust = null;
  }

  private seedNearPlayer(px: number, pz: number, camX?: number, camZ?: number): void {
    for (let i = 0, p = 0; i < this.driftField.pos.length; i += 3, p++) {
      respawnDriftLeaf(
        this.driftField.pos,
        this.driftField.vel,
        this.driftField.spin,
        this.driftField.variant,
        i,
        p,
        px,
        pz,
        this.driftField.wind,
        this.mapW,
        this.mapH,
      );
    }
    this.driftField.points.geometry.attributes.position!.needsUpdate = true;
    const awayFromCam =
      camX !== undefined && camZ !== undefined
        ? Math.atan2(pz - camZ, px - camX)
        : null;
    for (const layer of this.layers) {
      const { pos, vel, life, lifeSpan, yMin, yMax, kind } = layer;
      const radius = spawnRadius(kind);
      for (let i = 0, p = 0; i < pos.length; i += 3, p++) {
        placeNear(pos, vel, life, lifeSpan, i, p, px, pz, radius, yMin, yMax, kind, null, awayFromCam);
        // Stagger so the cloud doesn't pop in together.
        life[p] = Math.random() * 0.85;
      }
      layer.points.geometry.attributes.position!.needsUpdate = true;
      layer.points.geometry.attributes.aLife!.needsUpdate = true;
    }
  }

  private addLayer(
    count: number,
    color: number,
    /** Approximate pixel size at typical park camera distance. */
    pixelSize: number,
    additive: boolean,
    kind: LayerKind,
    yMin: number,
    yMax: number,
  ): void {
    const pos = new Float32Array(count * 3);
    const vel = new Float32Array(count * 3);
    const life = new Float32Array(count);
    const lifeSpan = new Float32Array(count);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geometry.setAttribute("aLife", new THREE.BufferAttribute(life, 1));

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(color) },
        uSize: { value: pixelSize },
        uOpacity: { value: additive ? 0.68 : 0.48 },
      },
      transparent: true,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      vertexShader: /* glsl */ `
        attribute float aLife;
        uniform float uSize;
        varying float vFade;

        void main() {
          // Ease in, hold, ease out - scale & alpha share this curve.
          float appear = smoothstep(0.0, 0.18, aLife);
          float vanish = 1.0 - smoothstep(0.72, 1.0, aLife);
          float grow = appear * vanish;
          vFade = grow;

          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          // Gentle size attenuation; hard cap so motes never become screen blobs.
          float atten = clamp(18.0 / max(10.0, -mvPosition.z), 0.45, 1.35);
          gl_PointSize = clamp(uSize * grow * atten, 0.5, 10.0);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        uniform float uOpacity;
        varying float vFade;

        void main() {
          if (vFade < 0.001) discard;
          vec2 c = gl_PointCoord - vec2(0.5);
          float d = length(c);
          if (d > 0.5) discard;
          float soft = 1.0 - smoothstep(0.2, 0.5, d);
          gl_FragColor = vec4(uColor, uOpacity * vFade * soft);
        }
      `,
    });

    const points = new THREE.Points(geometry, material);
    points.frustumCulled = false;
    this.layers.push({ points, pos, vel, life, lifeSpan, kind, yMin, yMax, age: 0 });
    this.root.add(points);
  }
}

type LayerKind = "firefly" | "fireflyFar" | "petal" | "butterfly" | "pollen" | "mist";

interface Layer {
  points: THREE.Points;
  pos: Float32Array;
  vel: Float32Array;
  /** 0 → 1 progress through this mote's life. */
  life: Float32Array;
  /** Seconds for a full life cycle. */
  lifeSpan: Float32Array;
  kind: LayerKind;
  yMin: number;
  yMax: number;
  age: number;
}

interface LeafGust {
  points: THREE.Points;
  pos: Float32Array;
  vel: Float32Array;
  life: Float32Array;
  spin: Float32Array;
  variant: Float32Array;
  age: number;
  duration: number;
}

const LEAF_COLORS = [0xd4a84a, 0xc87830, 0xb86b3a, 0xa85828, 0xe8b040, 0x8fbf5a, 0xcc7040];

let sharedLeafTexture: THREE.Texture | null = null;

/** 2×2 atlas of autumn leaf silhouettes (white + alpha, tinted per particle). */
function leafSpriteTexture(): THREE.Texture {
  if (sharedLeafTexture) {
    return sharedLeafTexture;
  }
  const cell = 64;
  const canvas = document.createElement("canvas");
  canvas.width = cell * 2;
  canvas.height = cell * 2;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    paintLeafCell(ctx, 0, 0, cell, "maple");
    paintLeafCell(ctx, cell, 0, cell, "oak");
    paintLeafCell(ctx, 0, cell, cell, "birch");
    paintLeafCell(ctx, cell, cell, cell, "round");
  }
  sharedLeafTexture = new THREE.CanvasTexture(canvas);
  sharedLeafTexture.colorSpace = THREE.SRGBColorSpace;
  sharedLeafTexture.magFilter = THREE.LinearFilter;
  sharedLeafTexture.minFilter = THREE.LinearMipMapLinearFilter;
  sharedLeafTexture.generateMipmaps = true;
  return sharedLeafTexture;
}

type LeafSilhouette = "maple" | "oak" | "birch" | "round";

function paintLeafCell(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  size: number,
  kind: LeafSilhouette,
): void {
  ctx.save();
  ctx.clearRect(ox, oy, size, size);
  ctx.translate(ox + size * 0.5, oy + size * 0.52);
  ctx.scale(size * 0.44, size * 0.44);

  ctx.beginPath();
  switch (kind) {
    case "maple":
      ctx.moveTo(0, -1.02);
      ctx.lineTo(0.28, -0.42);
      ctx.lineTo(0.82, -0.62);
      ctx.lineTo(0.34, -0.08);
      ctx.lineTo(0.92, 0.08);
      ctx.lineTo(0.28, 0.22);
      ctx.lineTo(0.42, 0.92);
      ctx.lineTo(0, 0.52);
      ctx.lineTo(-0.42, 0.92);
      ctx.lineTo(-0.28, 0.22);
      ctx.lineTo(-0.92, 0.08);
      ctx.lineTo(-0.34, -0.08);
      ctx.lineTo(-0.82, -0.62);
      ctx.lineTo(-0.28, -0.42);
      break;
    case "oak":
      ctx.moveTo(0, -0.98);
      for (let i = 0; i < 8; i++) {
        const a = -Math.PI * 0.5 + (i / 7) * Math.PI;
        const r = 0.38 + (i % 2 === 0 ? 0.22 : 0.08);
        ctx.lineTo(Math.cos(a) * r * 1.05, Math.sin(a) * r * 1.15 - 0.05);
      }
      ctx.lineTo(0, 0.82);
      ctx.lineTo(-0.08, 0.98);
      ctx.lineTo(0.08, 0.98);
      break;
    case "birch":
      ctx.moveTo(0, -1.05);
      ctx.bezierCurveTo(0.42, -0.55, 0.48, 0.05, 0.22, 0.72);
      ctx.quadraticCurveTo(0.08, 0.95, 0, 0.88);
      ctx.quadraticCurveTo(-0.08, 0.95, -0.22, 0.72);
      ctx.bezierCurveTo(-0.48, 0.05, -0.42, -0.55, 0, -1.05);
      break;
    case "round":
      ctx.moveTo(0, -0.88);
      ctx.bezierCurveTo(0.72, -0.72, 0.82, 0.18, 0.38, 0.78);
      ctx.quadraticCurveTo(0.1, 0.98, 0, 0.92);
      ctx.quadraticCurveTo(-0.1, 0.98, -0.38, 0.78);
      ctx.bezierCurveTo(-0.82, 0.18, -0.72, -0.72, 0, -0.88);
      break;
  }
  ctx.closePath();

  const fill = ctx.createRadialGradient(0, -0.15, 0.05, 0, 0.05, 1.05);
  fill.addColorStop(0, "rgba(255, 248, 230, 0.98)");
  fill.addColorStop(0.55, "rgba(255, 255, 255, 0.92)");
  fill.addColorStop(1, "rgba(255, 255, 255, 0.55)");
  ctx.fillStyle = fill;
  ctx.fill();

  ctx.strokeStyle = "rgba(55, 35, 18, 0.42)";
  ctx.lineWidth = 0.07;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(0, kind === "oak" ? -0.72 : -0.88);
  ctx.lineTo(0, kind === "birch" ? 0.82 : 0.68);
  ctx.stroke();

  if (kind === "maple") {
    for (const sx of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(0, -0.05);
      ctx.lineTo(sx * 0.55, sx * 0.18);
      ctx.stroke();
    }
  } else if (kind === "oak") {
    for (let i = -2; i <= 2; i++) {
      if (i === 0) continue;
      const t = i * 0.22;
      ctx.beginPath();
      ctx.moveTo(0, t);
      ctx.lineTo(i * 0.38, t + 0.18);
      ctx.stroke();
    }
  } else if (kind === "birch") {
    ctx.beginPath();
    ctx.moveTo(0, -0.2);
    ctx.lineTo(0.32, 0.08);
    ctx.moveTo(0, 0.05);
    ctx.lineTo(-0.28, 0.32);
    ctx.stroke();
  }

  ctx.restore();
}

const LEAF_DRIFT_VERTEX_SHADER = /* glsl */ `
  attribute float aSpin;
  attribute float aVariant;
  attribute vec3 aColor;
  uniform float uSize;
  uniform float uAge;
  varying float vFade;
  varying float vSpin;
  varying float vVariant;
  varying vec3 vColor;

  void main() {
    vSpin = aSpin + uAge * 1.8;
    vVariant = aVariant;
    vColor = aColor;
    vFade = 0.72 + 0.28 * sin(uAge * 0.9 + aSpin);

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float atten = clamp(22.0 / max(8.0, -mvPosition.z), 0.55, 1.55);
    gl_PointSize = clamp(uSize * atten, 2.0, 18.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const LEAF_FRAGMENT_SHADER = /* glsl */ `
  uniform sampler2D uLeafMap;
  varying float vFade;
  varying float vSpin;
  varying float vVariant;
  varying vec3 vColor;

  void main() {
    if (vFade < 0.04) discard;

    vec2 c = gl_PointCoord - vec2(0.5);
    float cs = cos(vSpin);
    float sn = sin(vSpin);
    vec2 r = vec2(cs * c.x - sn * c.y, sn * c.x + cs * c.y) + vec2(0.5);

    float col = mod(vVariant, 2.0);
    float row = floor(vVariant * 0.5);
    vec2 atlas = vec2(col * 0.5 + r.x * 0.5, row * 0.5 + r.y * 0.5);

    vec4 tex = texture2D(uLeafMap, atlas);
    if (tex.a < 0.08) discard;

    vec3 colTint = mix(vColor * 0.82, vColor * 1.12, tex.r);
    float alpha = tex.a * vFade * (0.82 + tex.r * 0.12);
    if (alpha < 0.03) discard;
    gl_FragColor = vec4(colTint, alpha);
  }
`;

const LEAF_GUST_VERTEX_SHADER = /* glsl */ `
  attribute float aLife;
  attribute float aSpin;
  attribute float aVariant;
  attribute vec3 aColor;
  uniform float uSize;
  uniform float uAge;
  uniform float uDuration;
  varying float vFade;
  varying float vSpin;
  varying float vVariant;
  varying vec3 vColor;

  void main() {
    float t = clamp(uAge / max(0.001, uDuration), 0.0, 1.0);
    float envelope = smoothstep(0.0, 0.12, t) * (1.0 - smoothstep(0.72, 1.0, t));
    float local = clamp(aLife, 0.0, 1.0);
    float appear = smoothstep(0.0, 0.2, local);
    float vanish = 1.0 - smoothstep(0.75, 1.0, local);
    vFade = envelope * appear * vanish;
    vSpin = aSpin + uAge * 2.4;
    vVariant = aVariant;
    vColor = aColor;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float atten = clamp(24.0 / max(8.0, -mvPosition.z), 0.55, 1.75);
    gl_PointSize = clamp(uSize * (0.82 + vFade * 0.45) * atten, 2.0, 24.0);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

interface LeafDriftField {
  points: THREE.Points;
  pos: Float32Array;
  vel: Float32Array;
  spin: Float32Array;
  variant: Float32Array;
  age: number;
  wind: number;
  lowQuality: boolean;
}

/** Low tumbling leaves that follow the pup through the walkable park. */
function createLeafDriftField(count: number): LeafDriftField {
  const pos = new Float32Array(count * 3);
  const vel = new Float32Array(count * 3);
  const spin = new Float32Array(count);
  const variant = new Float32Array(count);
  const color = new Float32Array(count * 3);

  for (let i = 0, p = 0; i < pos.length; i += 3, p++) {
    spin[p] = (Math.random() - 0.5) * 8;
    variant[p] = (p + (Math.random() * 4) | 0) % 4;
    const c = new THREE.Color(LEAF_COLORS[p % LEAF_COLORS.length]!);
    color[i] = c.r;
    color[i + 1] = c.g;
    color[i + 2] = c.b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geometry.setAttribute("aSpin", new THREE.BufferAttribute(spin, 1));
  geometry.setAttribute("aVariant", new THREE.BufferAttribute(variant, 1));
  geometry.setAttribute("aColor", new THREE.BufferAttribute(color, 3));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uLeafMap: { value: leafSpriteTexture() },
      uSize: { value: 13 },
      uAge: { value: 0 },
      uDuration: { value: 1 },
      uFadeEnvelope: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    vertexShader: LEAF_DRIFT_VERTEX_SHADER,
    fragmentShader: LEAF_FRAGMENT_SHADER,
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  points.renderOrder = 1;

  return {
    points,
    pos,
    vel,
    spin,
    variant,
    age: 0,
    wind: Math.random() * Math.PI * 2,
    lowQuality: false,
  };
}

function respawnDriftLeaf(
  pos: Float32Array,
  vel: Float32Array,
  spin: Float32Array,
  variant: Float32Array,
  i: number,
  p: number,
  px: number,
  pz: number,
  wind: number,
  mapW: number,
  mapH: number,
): void {
  const wx = Math.cos(wind);
  const wz = Math.sin(wind);
  const sx = -wz;
  const sz = wx;
  const radius = 10 + Math.random() * 14;
  const ang = Math.random() * Math.PI * 2;
  const across = Math.cos(ang) * radius;
  const along = (Math.random() - 0.5) * 8;
  pos[i] = px + sx * across + wx * along;
  // Mix ground tumblers with mid-air flutter so leaves aren't a floor carpet.
  pos[i + 1] = Math.random() < 0.45 ? 0.12 + Math.random() * 1.2 : 1.4 + Math.random() * 3.6;
  pos[i + 2] = pz + sz * across + wz * along;

  const speed = 0.55 + Math.random() * 0.85;
  vel[i] = wx * speed + (Math.random() - 0.5) * 0.35;
  vel[i + 1] = -(0.04 + Math.random() * 0.08);
  vel[i + 2] = wz * speed + (Math.random() - 0.5) * 0.35;
  spin[p] = (Math.random() - 0.5) * 9;
  if (Math.random() < 0.4) {
    variant[p] = (Math.random() * 4) | 0;
  }

  // Keep inside the park footprint.
  pos[i] = Math.max(1, Math.min(mapW - 1, pos[i]!));
  pos[i + 2] = Math.max(1, Math.min(mapH - 1, pos[i + 2]!));
}

function stepLeafDriftField(
  field: LeafDriftField,
  dt: number,
  playerX: number,
  playerZ: number,
  mapW: number,
  mapH: number,
): void {
  field.age += dt;
  field.wind += dt * 0.04;
  const wx = Math.cos(field.wind);
  const wz = Math.sin(field.wind);
  const recycleR = field.lowQuality ? 16 : 20;
  const recycleSq = recycleR * recycleR;

  for (let i = 0, p = 0; i < field.pos.length; i += 3, p++) {
    const phase = p * 0.37 + field.age;
    const flutter = Math.sin(phase * 8.5 + field.spin[p]!) * 0.55;
    field.pos[i]! += (field.vel[i]! + wx * 0.25 + flutter) * dt;
    field.pos[i + 1]! += field.vel[i + 1]! * dt;
    field.pos[i + 2]! += (field.vel[i + 2]! + wz * 0.25 + Math.cos(phase * 7.2) * 0.35) * dt;

    if (field.pos[i + 1]! < 0.08) {
      field.pos[i + 1] = 0.08;
      field.vel[i + 1] = Math.abs(field.vel[i + 1]!) * 0.35;
    }

    const dx = field.pos[i]! - playerX;
    const dz = field.pos[i + 2]! - playerZ;
    const oob =
      field.pos[i]! < 0.5
      || field.pos[i]! > mapW - 0.5
      || field.pos[i + 2]! < 0.5
      || field.pos[i + 2]! > mapH - 0.5;
    if (dx * dx + dz * dz > recycleSq || oob) {
      respawnDriftLeaf(
        field.pos,
        field.vel,
        field.spin,
        field.variant,
        i,
        p,
        playerX,
        playerZ,
        field.wind,
        mapW,
        mapH,
      );
    }
  }

  const mat = field.points.material as THREE.ShaderMaterial;
  mat.uniforms.uAge!.value = field.age;
  field.points.geometry.attributes.position!.needsUpdate = true;
}

/** Park-wide rustling leaf sheet that drifts through once, then clears. */
function makeLeafGust(
  mapW: number,
  mapH: number,
  px: number,
  pz: number,
  lowQuality: boolean,
): LeafGust {
  const count = lowQuality ? 56 : 96;
  const pos = new Float32Array(count * 3);
  const vel = new Float32Array(count * 3);
  const life = new Float32Array(count);
  const spin = new Float32Array(count);
  const variant = new Float32Array(count);
  const color = new Float32Array(count * 3);

  // Prefer a soft diagonal breeze - nostalgic autumn park wind.
  const wind =
    Math.random() < 0.5
      ? Math.PI * 0.18 + (Math.random() - 0.5) * 0.35
      : Math.PI * 1.05 + (Math.random() - 0.5) * 0.4;
  const wx = Math.cos(wind);
  const wz = Math.sin(wind);
  const sx = -wz;
  const sz = wx;
  const speed = 5.5 + Math.random() * 2.8;
  const span = Math.max(mapW, mapH) * 0.85;
  // Start the curtain upwind of the pup so it sweeps through the lawns.
  const startX = px - wx * span * 0.48;
  const startZ = pz - wz * span * 0.48;

  for (let i = 0, p = 0; i < pos.length; i += 3, p++) {
    const across = (Math.random() - 0.5) * span * 1.45;
    const along = (Math.random() - 0.5) * 12;
    pos[i] = startX + sx * across + wx * along;
    pos[i + 1] = 0.1 + Math.random() * 1.65;
    pos[i + 2] = startZ + sz * across + wz * along;

    const rush = speed * (0.82 + Math.random() * 0.4);
    vel[i] = wx * rush + (Math.random() - 0.5) * 1.4;
    vel[i + 1] = (Math.random() - 0.55) * 0.9;
    vel[i + 2] = wz * rush + (Math.random() - 0.5) * 1.4;

    // Stagger arrival so the sheet feels like a rustling wave, not a wall.
    life[p] = -Math.random() * 0.35;
    spin[p] = (Math.random() - 0.5) * 10;
    variant[p] = (p + ((Math.random() * 4) | 0)) % 4;

    const c = new THREE.Color(LEAF_COLORS[(p + ((Math.random() * LEAF_COLORS.length) | 0)) % LEAF_COLORS.length]!);
    color[i] = c.r;
    color[i + 1] = c.g;
    color[i + 2] = c.b;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geometry.setAttribute("aLife", new THREE.BufferAttribute(life, 1));
  geometry.setAttribute("aSpin", new THREE.BufferAttribute(spin, 1));
  geometry.setAttribute("aVariant", new THREE.BufferAttribute(variant, 1));
  geometry.setAttribute("aColor", new THREE.BufferAttribute(color, 3));

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uLeafMap: { value: leafSpriteTexture() },
      uSize: { value: lowQuality ? 14 : 17 },
      uAge: { value: 0 },
      uDuration: { value: 1 },
      uFadeEnvelope: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    vertexShader: LEAF_GUST_VERTEX_SHADER,
    fragmentShader: LEAF_FRAGMENT_SHADER,
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  points.renderOrder = 2;

  return {
    points,
    pos,
    vel,
    life,
    spin,
    variant,
    age: 0,
    duration: 10.5 + Math.random() * 2.5,
  };
}

function stepLeafGust(gust: LeafGust, dt: number): void {
  gust.age += dt;
  const { pos, vel, life, spin } = gust;
  const progress = gust.age / gust.duration;

  for (let i = 0, p = 0; i < pos.length; i += 3, p++) {
    // Local life: wait (negative), then 0→1 while tumbling.
    if (life[p]! < 0) {
      life[p]! += dt * 1.4;
      if (life[p]! < 0) {
        continue;
      }
      life[p] = 0;
    }
    life[p]! += dt / (5.5 + (p % 7) * 0.35);

    const phase = p * 0.41 + gust.age;
    // Flutter / rustle - quick lateral wobble like dry leaves.
    const flutter = Math.sin(phase * 9.5 + spin[p]!) * 1.8;
    const bob = Math.sin(phase * 6.2) * 0.55;
    pos[i]! += (vel[i]! + flutter * 0.35) * dt;
    pos[i + 1]! += (vel[i + 1]! + bob) * dt;
    pos[i + 2]! += (vel[i + 2]! + Math.cos(phase * 8.1) * 0.55) * dt;

    // Settle a little as the gust ages.
    if (progress > 0.45 && pos[i + 1]! > 0.12) {
      pos[i + 1]! -= dt * (0.35 + progress * 0.8);
    }
    if (pos[i + 1]! < 0.08) {
      pos[i + 1] = 0.08;
      life[p] = Math.max(life[p]!, 0.82);
    }
  }

  const mat = gust.points.material as THREE.ShaderMaterial;
  mat.uniforms.uAge!.value = gust.age;
  mat.uniforms.uDuration!.value = gust.duration;
  gust.points.geometry.attributes.position!.needsUpdate = true;
  gust.points.geometry.attributes.aLife!.needsUpdate = true;
}

/** Cloud radius around the pup (tiles). */
function spawnRadius(kind: LayerKind): number {
  switch (kind) {
    case "mist":
      return 16;
    case "butterfly":
      return 22;
    case "petal":
      return 18;
    case "pollen":
      return 16;
    case "fireflyFar":
      return 52;
    case "firefly":
      return 28;
    default:
      return 16;
  }
}

function maxRange(kind: LayerKind): number {
  if (kind === "fireflyFar") {
    return spawnRadius(kind) * 1.55;
  }
  if (kind === "firefly") {
    return spawnRadius(kind) * 1.5;
  }
  return spawnRadius(kind) * 1.45;
}

function lifeDuration(kind: LayerKind): number {
  switch (kind) {
    case "mist":
      return 5.5 + Math.random() * 3.5;
    case "petal":
      return 4 + Math.random() * 3;
    case "butterfly":
      return 3.5 + Math.random() * 2.5;
    case "pollen":
      return 3.2 + Math.random() * 2.8;
    case "fireflyFar":
      // Distant motes drift longer so the horizon stays twinkly.
      return 5.5 + Math.random() * 4.5;
    default:
      // Fireflies linger a beat longer so the glow feels continuous.
      return 4.2 + Math.random() * 3.2;
  }
}

function placeNear(
  pos: Float32Array,
  vel: Float32Array,
  life: Float32Array,
  lifeSpan: Float32Array,
  i: number,
  p: number,
  px: number,
  pz: number,
  radius: number,
  yMin: number,
  yMax: number,
  kind: LayerKind,
  preferHeading: number | null,
  awayFromCam: number | null = null,
): void {
  let ang = Math.random() * Math.PI * 2;
  // Far / tall layers prefer the camera-away half-plane so they land in the
  // upper frame (past the pup toward the horizon), not underfoot.
  if (
    awayFromCam !== null &&
    (kind === "fireflyFar" || kind === "butterfly" || kind === "petal" || kind === "mist")
  ) {
    const spread = kind === "fireflyFar" ? 2.4 : 1.8;
    ang = awayFromCam + (Math.random() - 0.5) * spread;
  } else if (preferHeading !== null) {
    ang = preferHeading + (Math.random() - 0.5) * 1.4;
  }
  // Keep a clear pocket near the pup - spawn mostly in the outer ring.
  // Far fireflies bias even farther out so the mid/far lawn twinkles.
  const ring =
    kind === "fireflyFar" ? 0.48 + Math.sqrt(Math.random()) * 0.52
    : kind === "firefly" ? 0.4 + Math.sqrt(Math.random()) * 0.6
    : 0.55 + Math.sqrt(Math.random()) * 0.45;
  const dist = ring * radius;
  pos[i] = px + Math.cos(ang) * dist;
  // Bias tall layers upward so the canopy / upper frame gets motes too.
  const t = Math.random();
  const yT =
    kind === "fireflyFar" || kind === "butterfly" ? Math.pow(t, 0.55)
    : kind === "petal" || kind === "mist" ? Math.pow(t, 0.7)
    : t;
  pos[i + 1] = yMin + yT * (yMax - yMin);
  pos[i + 2] = pz + Math.sin(ang) * dist;

  const speed =
    kind === "butterfly" ? 0.55 + Math.random() * 0.65
    : kind === "petal" ? 0.25 + Math.random() * 0.35
    : kind === "mist" ? 0.08 + Math.random() * 0.12
    : kind === "pollen" ? 0.15 + Math.random() * 0.25
    : kind === "fireflyFar" ? 0.12 + Math.random() * 0.22
    : 0.18 + Math.random() * 0.3;
  const heading = Math.random() * Math.PI * 2;
  vel[i] = Math.cos(heading) * speed;
  vel[i + 1] =
    kind === "petal" ? -(0.12 + Math.random() * 0.2)
    : kind === "mist" ? (Math.random() - 0.5) * 0.04
    : (Math.random() - 0.5) * 0.12;
  vel[i + 2] = Math.sin(heading) * speed;

  life[p] = 0;
  lifeSpan[p] = lifeDuration(kind);
}

/** Reuse ambient motes as a short burst kicked up from a flower bed. */
function burstMotes(
  layer: Layer,
  x: number,
  z: number,
  count: number,
  intensity: number,
  kind: LayerKind,
): void {
  const n = Math.min(count, layer.life.length);
  const { pos, vel, life, lifeSpan, yMin, yMax } = layer;
  for (let k = 0; k < n; k++) {
    const p = Math.floor(Math.random() * layer.life.length);
    const i = p * 3;
    const ang = Math.random() * Math.PI * 2;
    const dist = 0.15 + Math.random() * (0.55 + intensity * 0.45);
    pos[i] = x + Math.cos(ang) * dist;
    pos[i + 1] =
      kind === "petal"
        ? 0.35 + Math.random() * (0.55 + intensity * 0.7)
        : yMin + Math.random() * (yMax - yMin) * (0.6 + intensity * 0.35);
    pos[i + 2] = z + Math.sin(ang) * dist;

    const speed = (kind === "petal" ? 0.55 : 0.7) * (0.7 + intensity);
    vel[i] = Math.cos(ang) * speed * (0.6 + Math.random());
    vel[i + 1] =
      kind === "petal"
        ? 0.35 + Math.random() * 0.55 * intensity
        : 0.45 + Math.random() * 0.85 * intensity;
    vel[i + 2] = Math.sin(ang) * speed * (0.6 + Math.random());

    life[p] = 0.02 + Math.random() * 0.08;
    lifeSpan[p] = (kind === "petal" ? 1.1 : 1.4) + Math.random() * 0.7 + intensity * 0.35;
  }
  layer.points.geometry.attributes.position!.needsUpdate = true;
  layer.points.geometry.attributes.aLife!.needsUpdate = true;
}

function stepLayer(
  layer: Layer,
  dt: number,
  playerX: number,
  playerZ: number,
  mapW: number,
  mapH: number,
  preferHeading: number | null,
  awayFromCam: number | null = null,
): void {
  layer.age += dt;
  const { pos, vel, life, lifeSpan, kind, yMin, yMax } = layer;
  const range = maxRange(kind);
  const rangeSq = range * range;
  const radius = spawnRadius(kind);
  const behindLimit = radius * 0.55;
  const behindLimitSq = behindLimit * behindLimit;
  const hasHeading = preferHeading !== null;
  const hx = hasHeading ? Math.cos(preferHeading!) : 0;
  const hz = hasHeading ? Math.sin(preferHeading!) : 0;

  for (let i = 0, p = 0; i < pos.length; i += 3, p++) {
    const span = Math.max(0.5, lifeSpan[p]!);
    life[p]! += dt / span;

    // Finished fade-out - respawn (scales back up from 0).
    if (life[p]! >= 1) {
      placeNear(
        pos,
        vel,
        life,
        lifeSpan,
        i,
        p,
        playerX,
        playerZ,
        radius,
        yMin,
        yMax,
        kind,
        preferHeading,
        awayFromCam,
      );
      continue;
    }

    const phase = p * 0.37 + layer.age;
    let vx = vel[i]!;
    let vy = vel[i + 1]!;
    let vz = vel[i + 2]!;
    if (kind === "firefly" || kind === "fireflyFar") {
      const drift = kind === "fireflyFar" ? 0.1 : 0.15;
      vx += Math.sin(phase * 1.4) * drift;
      vz += Math.cos(phase * 1.1) * drift;
      vy += Math.sin(phase * 2.2) * (kind === "fireflyFar" ? 0.05 : 0.08);
    } else if (kind === "butterfly") {
      vx += Math.sin(phase * 2.6) * 0.35;
      vz += Math.cos(phase * 2.1) * 0.3;
      vy += Math.abs(Math.sin(phase * 3.2)) * 0.12;
    } else if (kind === "petal") {
      vx += Math.sin(phase * 0.9) * 0.1;
      vz += Math.cos(phase * 0.7) * 0.12;
    } else if (kind === "pollen") {
      vx += Math.sin(phase * 1.6) * 0.08;
      vz += Math.cos(phase * 1.3) * 0.08;
    }

    pos[i]! += vx * dt;
    pos[i + 1]! += vy * dt;
    pos[i + 2]! += vz * dt;

    if (kind === "petal" && pos[i + 1]! < 0.2) {
      // Soft land: finish life so it scales out, then recycle next ticks.
      life[p] = Math.max(life[p]!, 0.85);
    }

    if (kind !== "petal") {
      if (pos[i + 1]! < yMin) {
        pos[i + 1] = yMin;
        vel[i + 1] = Math.abs(vel[i + 1]!);
      } else if (pos[i + 1]! > yMax) {
        pos[i + 1] = yMax;
        vel[i + 1] = -Math.abs(vel[i + 1]!);
      }
    }

    const dx = pos[i]! - playerX;
    const dz = pos[i + 2]! - playerZ;
    const distSq = dx * dx + dz * dz;
    const far = distSq > rangeSq;
    const oob =
      pos[i]! < -2 || pos[i]! > mapW + 2 || pos[i + 2]! < -2 || pos[i + 2]! > mapH + 2;
    const behind =
      hasHeading && distSq > behindLimitSq && dx * hx + dz * hz < -behindLimit * 0.35;

    // Force a soft exit before hard teleport when leaving the bubble.
    if ((far || oob || behind) && life[p]! < 0.78) {
      life[p] = 0.78;
    }
  }

  layer.points.geometry.attributes.position!.needsUpdate = true;
  layer.points.geometry.attributes.aLife!.needsUpdate = true;
}
